import "./style.scss";
import React, { type CSSProperties, useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useAccountToDisplaySelector } from "../../redux/actions/user";
import { useJetpackConfigSelector } from "../../redux/jetpackConfig";
import CONFIG from "../../model/TSConfig";
import { LOGO_INPUT_ID } from "../SaasGameConfiguration/LogoConfigurator";

import ImageGrid from "./ImageGrid";
import { COLOR_LABELS } from "../../model/Colors";
import {
  setColorKey,
  useColorKeySelector
} from "../../redux/controller/controller";

interface Props {
  isPOSA: boolean;
  onChanged: (key: string, color: string) => void;
}

export default function (props: Props) {
  const dispatch = useDispatch();
  const jetpackConfig = useJetpackConfigSelector();
  const colorKey = useColorKeySelector();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const imgSrc = useMemo(() => {
    if (!jetpackConfig?.logo_url) {
      return {
        absolute: `${CONFIG.image_placeholder_url}`
      };
    }
    return {
      twic: `image:/cdm/jetpack/logos/${jetpackConfig.logo_url}`,
      absolute: `${CONFIG.cdn_url}/jetpack/logos/${jetpackConfig.logo_url}`
    };
  }, [jetpackConfig]);

  const [selectedColor, setSelectedColor] = React.useState<string | undefined>(
    ""
  );
  const [isMouseOver, setIsMouseOver] = React.useState(true);

  const handleColorPick = (color: string) => {
    if (!accountToDisplay?.trackingID) {
      return;
    }

    if (!colorKey || !color) {
      return;
    }

    props.onChanged(colorKey, color);
    dispatch(setColorKey(""));
  };

  const onSelectedColor = useCallback(async () => {
    if (!selectedColor || !colorKey || accountToDisplay == null) {
      return;
    }

    setSelectedColor("");
  }, [
    selectedColor,
    colorKey,
    accountToDisplay,
    jetpackConfig,
    dispatch,
    accountToDisplay?.token
  ]);

  const logoStyle = useMemo<CSSProperties>(() => {
    return {
      width: 108,
      height: "auto",
      border: "1px solid black",
      padding: 4,
      borderRadius: 6,
      cursor: `${!!colorKey && isMouseOver ? "crosshair" : ""}`
    };
  }, [imgSrc, colorKey, isMouseOver]);

  const colorKeyName = useMemo(() => {
    if (props.isPOSA) {
      // @ts-ignore
      return COLOR_LABELS.jpPOSA[colorKey];
    }
    // @ts-ignore
    return COLOR_LABELS.jpMobilePreview[colorKey];
  }, [colorKey, props.isPOSA]);

  const LOGO = useMemo(() => {
    if (imgSrc) {
      return (
        <>
          <canvas ref={canvasRef} style={{ display: "none" }} />
          <img
            ref={imageRef}
            alt={"logo"}
            id={"logo-preview"}
            src={imgSrc.absolute}
            onClick={async () => {
              if (!colorKey) {
                onLogoClicked();
              } else {
                await onSelectedColor();
              }
            }}
            style={logoStyle}
            onMouseEnter={() => {
              setIsMouseOver(true);
            }}
          />
        </>
      );
    }
    return (
      <img
        alt={"logo"}
        id={"logo-preview"}
        src={CONFIG.image_placeholder_url}
        onClick={onLogoClicked}
        style={logoStyle}
      />
    );
  }, [imgSrc, logoStyle, colorKey, onSelectedColor]);

  return (
    <div
      className={
        "logo-color-picker " +
        (!!jetpackConfig?.logo_url ? "visible" : "invisible")
      }
    >
      {LOGO}
      {!!colorKey && (
        <ImageGrid
          imageUrl={imgSrc.absolute ?? ""}
          onColorPick={handleColorPick}
          colorKeyName={colorKeyName}
          gridSize={10}
          onClose={() => dispatch(setColorKey(""))}
        />
      )}
    </div>
  );
}

function onLogoClicked() {
  document.getElementById(LOGO_INPUT_ID)?.click();
}
